import React from 'react';
import { Chip, CircularProgress } from '@mui/material';
import {
  BENCH_CANDIDATE_RUN_STATUS_LABELS,
  BENCH_CANDIDATE_RUN_STATUS_COLORS,
} from '../../../constants/benchCandidates';
import { HeaderStats, HeaderStat } from '../BenchEntryLagReport/styles';
import { getChipSx } from './chipStyles';
import { formatDateTime } from './dates';

/**
 * Last sync run as header stats — the same HeaderStat blocks the Bench Data Entry
 * Lag report shows in its header. Polling is handled by the parent hook.
 */
const LatestRunStrip = ({ run, isRunning }) => {
  if (!run) {
    return (
      <HeaderStats>
        <HeaderStat title='No sync run yet — the daily Americas pipeline populates this list, or use "Refresh".'>
          <div className="top">
            <span className="label">Last refresh</span>
            <span className="value">Never</span>
          </div>
        </HeaderStat>
      </HeaderStats>
    );
  }

  const warningCount = Array.isArray(run.warnings) ? run.warnings.length : 0;
  const statusLabel = run.stale ? 'Stale' : (BENCH_CANDIDATE_RUN_STATUS_LABELS[run.status] || run.status);
  const statusTitle = run.stale
    ? 'The snapshot never arrived for this run; it will be expired by the next refresh'
    : (run.status === 'failed' && run.errorMessage ? run.errorMessage : undefined);

  return (
    <HeaderStats>
      <HeaderStat>
        <div className="top">
          <span className="label">Last refresh</span>
          <span className="value" style={{ fontSize: 16 }}>{formatDateTime(run.startedAt)}</span>
        </div>
        <span className="sub">{run.triggeredBy}{run.triggeredByUser ? ` · ${run.triggeredByUser}` : ''}</span>
      </HeaderStat>
      <HeaderStat>
        <div className="top" style={{ alignItems: 'center' }}>
          <span className="label">Status</span>
          <Chip
            size="small"
            label={statusLabel}
            sx={getChipSx(run.stale ? 'warning' : BENCH_CANDIDATE_RUN_STATUS_COLORS[run.status])}
            title={statusTitle}
          />
          {isRunning && <CircularProgress size={14} />}
        </div>
        {warningCount > 0 && (
          <span className="sub" style={{ color: '#92400E', cursor: 'help' }} title={JSON.stringify(run.warnings, null, 2)}>
            {warningCount} warning{warningCount === 1 ? '' : 's'}
          </span>
        )}
      </HeaderStat>
      <HeaderStat>
        <div className="top">
          <span className="label">Fetched</span>
          <span className="value">{run.fetchedCount ?? 0}</span>
        </div>
      </HeaderStat>
      <HeaderStat>
        <div className="top">
          <span className="label">New / Updated</span>
          <span className="value">{run.createdCount ?? 0} / {run.updatedCount ?? 0}</span>
        </div>
      </HeaderStat>
      <HeaderStat>
        <div className="top">
          <span className="label">Removed / Back</span>
          <span className="value">{run.removedCount ?? 0} / {run.reappearedCount ?? 0}</span>
        </div>
      </HeaderStat>
      <HeaderStat>
        <div className="top">
          <span className="label">Not in MySM</span>
          <span className="value">{run.unmatchedCount ?? 0}</span>
        </div>
      </HeaderStat>
    </HeaderStats>
  );
};

export default LatestRunStrip;
